<template>
  <div v-if="relatedEquipments.length" class="mt-6">
    <h3 class="text-xl sm:text-2xl font-bold mb-3 text-theme-text flex items-center tracking-tight">
      <i class="pi pi-th-large mr-2 text-primary-600"></i> You May Also Like
    </h3>

    <!-- Grid Layout for Medium and Larger Screens -->
    <div 
      v-if="!isSmallScreen" 
      class="grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 sm:gap-4"
    >
      <div 
        v-for="equipment in relatedEquipments" 
        :key="equipment.id" 
        class="bg-theme-card rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer flex flex-col h-full relative group border border-theme-border"
      >
        <!-- Image -->
        <div @click="goToDetail(equipment.id)" class="relative w-full aspect-[4/3] overflow-hidden bg-theme-bg2">
          <img
            v-if="equipment.images && equipment.images.length > 0"
            :src="getFullImageUrl(equipment.images[0].image_url)"
            :alt="`Image of ${equipment.name}`"
            class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
          <div v-else class="absolute inset-0 flex items-center justify-center">
            <i class="pi pi-image text-3xl text-theme-muted"></i>
          </div>
          <span
            v-if="!equipment.is_available || equipment.available_quantity === 0"
            class="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/90 text-white"
          >
            Unavailable
          </span>
        </div>

        <!-- Content -->
        <div class="px-2 pt-1.5 pb-2 sm:px-3 sm:pt-2.5 sm:pb-3 flex flex-col flex-grow gap-1 sm:gap-1.5">
          <!-- Owner row -->
          <div class="flex items-center gap-1 sm:gap-1.5">
            <img
              v-if="equipment.owner?.image_url"
              :src="equipment.owner.image_url"
              :alt="getOwnerName(equipment)"
              class="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-theme-border"
            />
            <div v-else class="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary-600">
              {{ getOwnerInitial(equipment) }}
            </div>
            <span class="text-xs sm:text-sm text-theme-text2 truncate flex-1">{{ getOwnerName(equipment) }}</span>
            <i v-if="equipment.owner?.is_verified" class="pi pi-verified text-xs sm:text-sm text-primary-500" title="Verified"></i>
          </div>

          <!-- Item name -->
          <h5 class="text-xs sm:text-sm font-semibold text-theme-text line-clamp-1 leading-snug">
            {{ store.truncateText(equipment.name, 20) }}
          </h5>

          <!-- Rating + Distance row -->
          <div class="flex items-center justify-between text-[11px] sm:text-sm">
            <div class="flex items-center gap-0.5 sm:gap-1 min-w-0">
              <span class="text-yellow-500 hidden sm:inline">{{ renderStars(equipment.rating) }}</span>
              <span class="text-yellow-500 sm:hidden">★</span>
              <span class="text-theme-text2 truncate">{{ (equipment.rating || 0).toFixed(1) }} ({{ equipment.equipment_reviews?.length || 0 }})</span>
            </div>
            <span v-if="getDistance(equipment)" class="inline-flex items-center gap-0.5 font-semibold text-theme-text text-[10px] sm:text-sm shrink-0 ml-1">
              <i class="pi pi-map-marker text-[9px] sm:text-xs text-primary-500"></i>
              {{ getDistance(equipment) }}
            </span>
          </div>

          <!-- Pricing tiers -->
          <div class="mt-auto">
            <div class="flex items-center gap-1 mb-1">
              <button
                v-for="tier in [{key:'day',label:'Day'},{key:'week',label:'Week'},{key:'month',label:'Month'}]"
                :key="tier.key"
                @click.stop="setActiveRate(equipment.id, tier.key)"
                class="px-1.5 py-0.5 sm:px-2 rounded-full text-[10px] sm:text-xs font-semibold transition-colors"
                :class="getActiveRate(equipment.id) === tier.key
                  ? 'bg-primary-500 text-white'
                  : 'bg-theme-bg2 text-theme-text2 hover:bg-theme-bg2/80'"
              >
                {{ tier.label }}
              </button>
            </div>
            <p class="text-primary-600 font-extrabold text-sm sm:text-base leading-tight">
              {{ formatPrice(getCurrentRate(equipment)) }}
              <span class="text-theme-muted text-[10px] sm:text-xs font-normal">/ {{ getRatePeriod(equipment.id) }}</span>
            </p>
          </div>

          <!-- CTA -->
          <button 
            @click="goToDetail(equipment.id)" 
            class="mt-0.5 sm:mt-1 w-full py-1 sm:py-1.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-600"
          >
            View Details
          </button>
        </div>
      </div>
    </div>

    <!-- Paginated Layout for Small Screens -->
    <div v-else>
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-4">
        <div 
          v-for="equipment in paginatedEquipments" 
          :key="equipment.id" 
          class="bg-theme-card rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer flex flex-col h-full relative group border border-theme-border"
        >
          <!-- Image -->
          <div @click="goToDetail(equipment.id)" class="relative w-full aspect-[4/3] overflow-hidden bg-theme-bg2">
            <img
              v-if="equipment.images && equipment.images.length > 0"
              :src="getFullImageUrl(equipment.images[0].image_url)"
              :alt="equipment.name"
              class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
            <div v-else class="absolute inset-0 flex items-center justify-center">
              <i class="pi pi-image text-2xl text-theme-muted"></i>
            </div>
            <span
              v-if="!equipment.is_available || equipment.available_quantity === 0"
              class="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/90 text-white"
            >
              Unavailable
            </span>
          </div>

          <!-- Content -->
          <div class="px-2 pt-1.5 pb-2 sm:px-3 sm:pt-2.5 sm:pb-3 flex flex-col flex-grow gap-1 sm:gap-1.5">
            <!-- Owner row -->
            <div class="flex items-center gap-1 sm:gap-1.5">
              <img
                v-if="equipment.owner?.image_url"
                :src="equipment.owner.image_url"
                :alt="getOwnerName(equipment)"
                class="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover border border-theme-border"
              />
              <div v-else class="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary-600">
                {{ getOwnerInitial(equipment) }}
              </div>
              <span class="text-xs sm:text-sm text-theme-text2 truncate flex-1">{{ getOwnerName(equipment) }}</span>
              <i v-if="equipment.owner?.is_verified" class="pi pi-verified text-xs sm:text-sm text-primary-500" title="Verified"></i>
            </div>

            <!-- Item name -->
            <h5 class="text-xs sm:text-sm font-semibold text-theme-text line-clamp-1 leading-snug">
              {{ store.truncateText(equipment.name, 20) }}
            </h5>

            <!-- Rating + Distance row -->
            <div class="flex items-center justify-between text-[11px] sm:text-sm">
              <div class="flex items-center gap-0.5 sm:gap-1 min-w-0">
                <span class="text-yellow-500 hidden sm:inline">{{ renderStars(equipment.rating) }}</span>
                <span class="text-yellow-500 sm:hidden">★</span>
                <span class="text-theme-text2 truncate">{{ (equipment.rating || 0).toFixed(1) }} ({{ equipment.equipment_reviews?.length || 0 }})</span>
              </div>
              <span v-if="getDistance(equipment)" class="inline-flex items-center gap-0.5 font-semibold text-theme-text text-[10px] sm:text-sm shrink-0 ml-1">
                <i class="pi pi-map-marker text-[9px] sm:text-xs text-primary-500"></i>
                {{ getDistance(equipment) }}
              </span>
            </div>

            <!-- Pricing tiers -->
            <div class="mt-auto">
              <div class="flex items-center gap-1 mb-1">
                <button
                  v-for="tier in [{key:'day',label:'Day'},{key:'week',label:'Week'},{key:'month',label:'Month'}]"
                  :key="tier.key"
                  @click.stop="setActiveRate(equipment.id, tier.key)"
                  class="px-1.5 py-0.5 sm:px-2 rounded-full text-[10px] sm:text-xs font-semibold transition-colors"
                  :class="getActiveRate(equipment.id) === tier.key
                    ? 'bg-primary-500 text-white'
                    : 'bg-theme-bg2 text-theme-text2 hover:bg-theme-bg2/80'"
                >
                  {{ tier.label }}
                </button>
              </div>
              <p class="text-primary-600 font-extrabold text-sm sm:text-base leading-tight">
                {{ formatPrice(getCurrentRate(equipment)) }}
                <span class="text-theme-muted text-[10px] sm:text-xs font-normal">/ {{ getRatePeriod(equipment.id) }}</span>
              </p>
            </div>

            <!-- CTA -->
            <button 
              @click="goToDetail(equipment.id)" 
              class="mt-0.5 sm:mt-1 w-full py-1 sm:py-1.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-600"
            >
              View Details
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div v-if="relatedEquipments.length > itemsPerPage" class="flex justify-between mt-4">
        <button
          @click="prev"
          :disabled="currentIndex === 0"
          class="px-3 py-1 bg-neutral-200 text-theme-text2 rounded disabled:opacity-50 text-sm"
        >
          Previous
        </button>
        <button
          @click="next"
          :disabled="currentIndex + itemsPerPage >= relatedEquipments.length"
          class="px-3 py-1 bg-neutral-200 text-theme-text2 rounded disabled:opacity-50 text-sm"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted, computed, watch, reactive } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useEquipmentsStore } from "@/store/equipments";
import { useCurrency } from '@/composables/useCurrency';
import { useLocation } from '@/composables/useLocation';
import { resolveImageUrl } from '@/composables/useImageUrl';

const { formatPrice } = useCurrency();
const { getDistance } = useLocation();
const getFullImageUrl = resolveImageUrl;
const store = useEquipmentsStore();
const route = useRoute();
const router = useRouter();
const relatedEquipments = computed(() => store.relatedEquipments);

// Rate tier state per card
const activeRates = reactive({});
const getActiveRate = (id) => activeRates[id] || 'day';
const setActiveRate = (id, tier) => { activeRates[id] = tier; };
const getCurrentRate = (eq) => {
  const tier = getActiveRate(eq.id);
  if (tier === 'week') return eq.weekly_rate ?? eq.hourly_rate * 7 * 0.9;
  if (tier === 'month') return eq.monthly_rate ?? eq.hourly_rate * 30 * 0.8;
  return eq.hourly_rate;
};
const getRatePeriod = (id) => {
  const t = getActiveRate(id);
  return t === 'week' ? 'Week' : t === 'month' ? 'Month' : 'Day';
};

// Owner helpers
const getOwnerName = (eq) => {
  const o = eq.owner;
  if (!o) return 'Unknown';
  if (o.first_name || o.last_name) return `${o.first_name || ''} ${o.last_name || ''}`.trim();
  if (o.company_name) return o.company_name;
  return 'Unknown';
};
const getOwnerInitial = (eq) => getOwnerName(eq).charAt(0).toUpperCase();

// Render star ratings
const renderStars = (rating) => {
  const r = rating || 0;
  const full = Math.floor(r);
  const half = r % 1 >= 0.5 ? 1 : 0;
  return '★'.repeat(full) + (half ? '☆' : '') + '☆'.repeat(5 - full - half);
};

// Track screen size
const isSmallScreen = ref(window.innerWidth < 640);

// Listen for screen resize
window.addEventListener("resize", () => {
isSmallScreen.value = window.innerWidth < 640;
});

// Pagination variables
const currentIndex = ref(0);
const itemsPerPage = ref(3);

const paginatedEquipments = computed(() => {
if (!isSmallScreen.value) return relatedEquipments.value;
return relatedEquipments.value.slice(currentIndex.value, currentIndex.value + itemsPerPage.value);
});

// Pagination functions
const next = () => {
if (currentIndex.value + itemsPerPage.value < relatedEquipments.value.length) {
  currentIndex.value += itemsPerPage.value;
}
};

const prev = () => {
if (currentIndex.value > 0) {
  currentIndex.value -= itemsPerPage.value;
}
};

const fetchRelatedItems = async () => {
const equipmentId = route.params.id;
if (equipmentId) {
  await store.fetchRelatedEquipments(equipmentId);
}
};

const goToDetail = (equipmentId) => {
if (equipmentId) {
  router.push({ name: "equipment-details", params: { id: equipmentId } });
}
};

// Fetch data when the component mounts
onMounted(fetchRelatedItems);

// Watch for route changes and refetch data
watch(() => route.params.id, fetchRelatedItems);
</script>
